import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  RingProgress,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  ArrowRight,
  BarChart3,
  Bell,
  Building2,
  CalendarCheck,
  CalendarDays,
  ChartColumn,
  CircleCheck,
  ClipboardCheck,
  ClipboardList,
  ClipboardPlus,
  FileText,
  Folder,
  Fuel,
  Megaphone,
  Monitor,
  MonitorSmartphone,
  Receipt,
  RotateCw,
  Route as RouteIcon,
  Smartphone,
  TriangleAlert,
  UserPlus,
  UserRound,
  UserX,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Layout } from '../components/Layout.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  clientActivityAPI,
  dashboardAPI,
  odooQueueAPI,
} from '../services/api.ts';
import SalesGraph from '../components/SalesGraph.tsx';
import UsersGraph from '../components/UsersGraph.tsx';
import { toast } from '../lib/toast.ts';

// The home page's look: large rounded surfaces that sit softly above the
// page, with a wide diffuse shadow and no border. Each value adapts to the
// colour scheme.
const SURFACE =
  'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))';
const SHADOW_LG =
  '0 30px 60px -20px light-dark(rgba(100, 116, 139, 0.35), rgba(0, 0, 0, 0.65))';
const SHADOW_MD =
  '0 16px 32px -14px light-dark(rgba(100, 116, 139, 0.32), rgba(0, 0, 0, 0.6))';
const SHADOW_SM =
  '0 8px 18px -8px light-dark(rgba(100, 116, 139, 0.35), rgba(0, 0, 0, 0.6))';

const raised = (shadow: string, radius: number) => ({
  background: SURFACE,
  boxShadow: shadow,
  borderRadius: radius,
});

// Small all-caps caption, as used on tiles, pills and stat labels.
const CAPTION = { fw: 700, tt: 'uppercase' as const, lts: 1.5 };

// Where each module's records are listed, and its icon.
const MODULES: Record<string, { href: string; icon: LucideIcon }> = {
  sales: { href: '/sales', icon: BarChart3 },
  expense: { href: '/expense', icon: Receipt },
  fuel: { href: '/fleet/fuel', icon: Fuel },
  maintenance: { href: '/fleet/maintenance', icon: Wrench },
  toll: { href: '/fleet/toll', icon: RouteIcon },
  personal_use: { href: '/fleet/personal-use', icon: UserRound },
  daily_activity: { href: '/daily-activity', icon: CalendarCheck },
  credit_limit_apply: { href: '/credit-limit-apply', icon: ClipboardList },
  credit_limit_extension: {
    href: '/credit-limit-extension',
    icon: ClipboardPlus,
  },
  temporary_credit: { href: '/temporary-credit', icon: ClipboardCheck },
};

const PERIODS = [
  { label: 'Today', value: '1' },
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
];

const greeting = () => {
  const hour = dayjs().hour();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

export const Dashboard: React.FC = () => {
  const { user, project } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === 'super_admin';

  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const [days, setDays] = useState('7');
  const [activity, setActivity] = useState<any[] | null>(null);

  const [queue, setQueue] = useState<{
    counts: Record<string, number>;
    failed: any[];
  } | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  // Who is on the app and who on the web. `false` once it could not be read.
  const [clients, setClients] = useState<any | false | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await dashboardAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    let current = true;
    setActivity(null);
    dashboardAPI
      .getActivity(Number(days))
      .then((response) => current && setActivity(response.data?.modules || []))
      .catch(() => current && setActivity([]));
    return () => {
      current = false;
    };
  }, [days]);

  const loadQueue = useCallback(async () => {
    try {
      const response = await odooQueueAPI.list({ status: 'failed', limit: 5 });
      setQueue({ counts: response.counts || {}, failed: response.data || [] });
    } catch {
      setQueue({ counts: {}, failed: [] });
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    let current = true;
    clientActivityAPI
      .summary()
      .then((response) => current && setClients(response.data || false))
      .catch(() => current && setClients(false));
    return () => {
      current = false;
    };
  }, []);

  // Keep the queue card live while requests are still on their way.
  const moving =
    (queue?.counts.queued || 0) + (queue?.counts.processing || 0) > 0;
  useEffect(() => {
    if (!moving) return;
    const timer = setInterval(loadQueue, 5000);
    return () => clearInterval(timer);
  }, [moving, loadQueue]);

  const retry = async (id: string | null) => {
    try {
      setRetrying(id || 'all');
      const response = await odooQueueAPI.retry(id ? { ids: [id] } : { all: true });
      toast.success(
        `${response.requeued} request${response.requeued === 1 ? '' : 's'} queued again`,
      );
      await loadQueue();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Could not retry');
    } finally {
      setRetrying(null);
    }
  };

  const statTiles = !stats
    ? []
    : isSuperAdmin
      ? [
          { name: 'Total Projects', stat: stats.totalProjects, icon: Folder, color: 'blue', href: '/projects' },
          { name: 'Active Projects', stat: stats.activeProjects, icon: ChartColumn, color: 'green', href: '/projects' },
          { name: 'Total Users', stat: stats.totalEmployees, icon: Users, color: 'grape' },
          { name: 'Active Users', stat: stats.activeEmployees, icon: CalendarDays, color: 'yellow' },
        ]
      : [
          { name: 'Total Employees', stat: stats.totalEmployees, icon: Users, color: 'grape', href: '/employees' },
          { name: 'Active Employees', stat: stats.activeEmployees, icon: CalendarDays, color: 'green', href: '/employees' },
          { name: 'Departments', stat: stats.departments, icon: Building2, color: 'blue' },
        ];

  const shortcuts = isSuperAdmin
    ? [
        { label: 'Projects', icon: Folder, href: '/projects' },
        { label: 'Odoo Configurations', icon: Wrench, href: '/odoo-configurations' },
        { label: 'Reports', icon: FileText, href: '/reports' },
        { label: 'Advertisement', icon: Megaphone, href: '/advertisement' },
      ]
    : [
        { label: 'Add a user', icon: UserPlus, href: '/employees' },
        { label: 'Send a notification', icon: Bell, href: '/notifications' },
        { label: 'Reports', icon: FileText, href: '/reports' },
        { label: 'Advertisement', icon: Megaphone, href: '/advertisement' },
      ];

  const counts = queue?.counts || {};
  const failed = counts.failed || 0;
  const waiting = (counts.queued || 0) + (counts.processing || 0);
  const sent = counts.synced || 0;
  const totalRequests = failed + waiting + sent;
  const share = (value: number) =>
    totalRequests ? (value / totalRequests) * 100 : 0;

  const clientTiles = !clients
    ? []
    : [
        { name: 'On the app', stat: clients.on_app, icon: Smartphone, color: 'blue' },
        { name: 'On the web', stat: clients.on_web, icon: Monitor, color: 'teal' },
        { name: 'On both', stat: clients.on_both, icon: MonitorSmartphone, color: 'grape' },
        { name: 'Never seen', stat: clients.never_seen, icon: UserX, color: 'gray' },
      ];
  const oldApp = clients ? clients.legacy?.app || 0 : 0;
  const oldWeb = clients ? clients.legacy?.web || 0 : 0;

  const activityTotal = (activity || []).reduce(
    (sum, module) => sum + module.count,
    0,
  );

  if (loading) {
    return (
      <Layout>
        <Group justify="center" h={320}>
          <Loader size="lg" />
        </Group>
      </Layout>
    );
  }

  return (
    <Layout>
      <Stack gap={40} py="sm">
        {/* Greeting */}
        <Group justify="space-between" align="center" gap="md">
          <Stack gap={4}>
            <Title order={1} fz={{ base: 26, sm: 34 }} fw={800} lts={-1}>
              {greeting()},{' '}
              <Text span inherit c="dimmed">
                {user?.name?.split(' ')[0] || 'Admin'}
              </Text>
            </Title>
            <Text c="dimmed" fw={500}>
              {isSuperAdmin
                ? 'Here is how every project is doing.'
                : `Here is how ${project?.name || 'your project'} is doing.`}
            </Text>
          </Stack>
          <Group gap="xs" px="lg" py={10} style={raised(SHADOW_SM, 999)}>
            <Text size="xs" c="dimmed" {...CAPTION}>
              {dayjs().format('dddd, D MMMM YYYY')}
            </Text>
          </Group>
        </Group>

        {/* Headline numbers; the ones with somewhere to go are links */}
        <SimpleGrid cols={{ base: 1, xs: 2, lg: statTiles.length }} spacing="xl">
          {statTiles.map((item) => {
            const tile = (
              <Box p="lg" h="100%" style={raised(SHADOW_MD, 28)}>
                <Group gap="md" wrap="nowrap">
                  <Center w={52} h={52} c={item.color} style={raised(SHADOW_SM, 20)}>
                    <item.icon size={24} aria-hidden="true" />
                  </Center>
                  <Stack gap={0} flex={1}>
                    <Text size="xs" c="dimmed" {...CAPTION}>
                      {item.name}
                    </Text>
                    <Text fz={30} fw={800} lh={1.2}>
                      {item.stat ?? 0}
                    </Text>
                  </Stack>
                  {item.href && <ArrowRight size={18} opacity={0.4} />}
                </Group>
              </Box>
            );
            return item.href ? (
              <UnstyledButton key={item.name} onClick={() => navigate(item.href!)}>
                {tile}
              </UnstyledButton>
            ) : (
              <Box key={item.name}>{tile}</Box>
            );
          })}
        </SimpleGrid>

        {/* Odoo queue health, with the latest refusals */}
        <Box p={{ base: 'lg', sm: 36 }} style={raised(SHADOW_LG, 36)}>
          <Group justify="space-between" align="center" mb="lg" gap="md">
            <Stack gap={2}>
              <Title order={2} fz={{ base: 22, sm: 26 }} fw={800} lts={-0.5}>
                Odoo Queue
              </Title>
              <Text c="dimmed" fw={500} size="sm">
                Requests from the apps on their way to Odoo.
              </Text>
            </Stack>
            <Group gap="sm">
              {failed > 0 && (
                <Button
                  variant="light"
                  leftSection={<RotateCw size={16} />}
                  loading={retrying === 'all'}
                  onClick={() => retry(null)}
                >
                  Retry all failed
                </Button>
              )}
              <Button
                variant="default"
                rightSection={<ArrowRight size={16} />}
                onClick={() => navigate('/odoo-queue')}
              >
                Open queue
              </Button>
            </Group>
          </Group>

          {!queue ? (
            <Skeleton h={180} radius="lg" />
          ) : (
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
              <Group gap="xl" wrap="nowrap">
                <RingProgress
                  size={170}
                  thickness={16}
                  roundCaps
                  sections={
                    totalRequests
                      ? [
                          { value: share(sent), color: 'green', tooltip: `${sent} sent` },
                          { value: share(waiting), color: 'blue', tooltip: `${waiting} waiting` },
                          { value: share(failed), color: 'red', tooltip: `${failed} failed` },
                        ]
                      : [{ value: 100, color: 'gray' }]
                  }
                  label={
                    <Stack gap={0} align="center">
                      <Text fz={26} fw={800} lh={1}>
                        {totalRequests}
                      </Text>
                      <Text size="xs" c="dimmed" {...CAPTION}>
                        Requests
                      </Text>
                    </Stack>
                  }
                />
                <Stack gap="sm" flex={1}>
                  {[
                    { label: 'Sent', value: sent, color: 'green' },
                    { label: 'Waiting', value: waiting, color: 'blue' },
                    { label: 'Failed', value: failed, color: 'red' },
                  ].map((row) => (
                    <Group key={row.label} justify="space-between">
                      <Group gap="xs">
                        <Box w={10} h={10} bg={row.color} style={{ borderRadius: '50%' }} />
                        <Text size="sm" fw={600}>
                          {row.label}
                        </Text>
                      </Group>
                      <Text fw={800}>{row.value}</Text>
                    </Group>
                  ))}
                </Stack>
              </Group>

              <Stack gap="sm">
                <Text size="xs" c="dimmed" {...CAPTION}>
                  Latest failed
                </Text>
                {queue.failed.length === 0 ? (
                  <Group gap="xs" c="green" py="md">
                    <CircleCheck size={18} />
                    <Text size="sm" fw={600}>
                      Nothing has failed.
                    </Text>
                  </Group>
                ) : (
                  queue.failed.map((row) => (
                    <Group
                      key={row._id}
                      justify="space-between"
                      wrap="nowrap"
                      gap="sm"
                      p="sm"
                      style={raised(SHADOW_SM, 18)}
                    >
                      <Box miw={0}>
                        <Text size="sm" fw={600} tt="capitalize" truncate>
                          {row.label}
                          {row.employeeName ? ` · ${row.employeeName}` : ''}
                        </Text>
                        <Text size="xs" c="red" truncate>
                          {row.lastError}
                        </Text>
                      </Box>
                      <Tooltip label="Send again, unchanged">
                        <Button
                          size="compact-sm"
                          variant="light"
                          loading={retrying === row._id}
                          disabled={!!retrying && retrying !== row._id}
                          onClick={() => retry(row._id)}
                        >
                          Retry
                        </Button>
                      </Tooltip>
                    </Group>
                  ))
                )}
              </Stack>
            </SimpleGrid>
          )}
        </Box>

        {/* Which client each user is on: the app, the web, or an old build */}
        {clients !== false && (
          <Box p={{ base: 'lg', sm: 36 }} style={raised(SHADOW_LG, 36)}>
            <Group justify="space-between" align="center" mb="lg" gap="md">
              <Stack gap={2}>
                <Title order={2} fz={{ base: 22, sm: 26 }} fw={800} lts={-0.5}>
                  Clients in use
                </Title>
                <Text c="dimmed" fw={500} size="sm">
                  {clients
                    ? `Which of the ${clients.employees_total} active users are on the mobile app and which on the web dashboard.`
                    : 'Who is on the mobile app and who on the web dashboard.'}
                </Text>
              </Stack>
              <Button
                variant="default"
                rightSection={<ArrowRight size={16} />}
                onClick={() => navigate('/user-activity')}
              >
                Open user activity
              </Button>
            </Group>

            {!clients ? (
              <Skeleton h={220} radius="lg" />
            ) : (
              <Stack gap="xl">
                <SimpleGrid cols={{ base: 2, md: 4 }} spacing={{ base: 'md', sm: 'lg' }}>
                  {clientTiles.map((item) => (
                    <Group
                      key={item.name}
                      gap="md"
                      wrap="nowrap"
                      p="md"
                      style={raised(SHADOW_MD, 24)}
                    >
                      <Center w={44} h={44} c={item.color} style={raised(SHADOW_SM, 16)}>
                        <item.icon size={20} aria-hidden="true" />
                      </Center>
                      <Stack gap={0} miw={0}>
                        <Text size="xs" c="dimmed" truncate {...CAPTION}>
                          {item.name}
                        </Text>
                        <Text fz={26} fw={800} lh={1.2}>
                          {item.stat ?? 0}
                        </Text>
                      </Stack>
                    </Group>
                  ))}
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
                  <Stack gap="sm">
                    <Text size="xs" c="dimmed" {...CAPTION}>
                      Active
                    </Text>
                    {[
                      { label: 'Today', value: clients.active_today },
                      { label: 'Last 7 days', value: clients.active_7d },
                    ].map((row) => (
                      <Group key={row.label} justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={600}>
                          {row.label}
                        </Text>
                        <Group gap="xs" wrap="nowrap">
                          <Tooltip label="Users seen on the app">
                            <Badge
                              color="blue"
                              variant="light"
                              leftSection={<Smartphone size={12} />}
                            >
                              {row.value?.app ?? 0}
                            </Badge>
                          </Tooltip>
                          <Tooltip label="Users seen on the web">
                            <Badge
                              color="teal"
                              variant="light"
                              leftSection={<Monitor size={12} />}
                            >
                              {row.value?.web ?? 0}
                            </Badge>
                          </Tooltip>
                        </Group>
                      </Group>
                    ))}
                  </Stack>

                  <Stack gap="sm">
                    <Text size="xs" c="dimmed" {...CAPTION}>
                      App versions in use
                    </Text>
                    {(clients.app_versions || []).length === 0 ? (
                      <Text size="sm" c="dimmed">
                        Nobody has used the app yet.
                      </Text>
                    ) : (
                      <Group gap="xs">
                        {clients.app_versions.map((item: any) => (
                          <Badge
                            key={`${item.version}-${item.legacy}`}
                            color={item.legacy ? 'orange' : 'blue'}
                            variant="light"
                            size="lg"
                            tt="none"
                          >
                            {item.legacy ? 'Old app' : item.version || 'Unknown'} ·{' '}
                            {item.count}
                          </Badge>
                        ))}
                      </Group>
                    )}
                    {(clients.app_platforms || []).length > 0 && (
                      <Text size="xs" c="dimmed">
                        {clients.app_platforms
                          .map(
                            (item: any) =>
                              `${{ android: 'Android', ios: 'iOS' }[item.platform as string] || item.platform || 'Unknown platform'} ${item.count}`,
                          )
                          .join(' · ')}
                      </Text>
                    )}
                  </Stack>

                  <Stack gap="sm">
                    <Text size="xs" c="dimmed" {...CAPTION}>
                      Still on the old app
                    </Text>
                    <Group gap="sm" wrap="nowrap" c={oldApp > 0 ? 'orange' : 'green'}>
                      {oldApp > 0 ? <TriangleAlert size={22} /> : <CircleCheck size={22} />}
                      <Text fz={26} fw={800} lh={1}>
                        {oldApp}
                      </Text>
                      <Text size="sm" fw={600}>
                        {oldApp === 0
                          ? 'Nobody is on the old app.'
                          : `user${oldApp === 1 ? '' : 's'} to move to the new app`}
                      </Text>
                    </Group>
                    {oldWeb > 0 && (
                      <Text size="xs" c="dimmed">
                        {oldWeb} user{oldWeb === 1 ? ' is' : 's are'} on the old
                        web dashboard.
                      </Text>
                    )}
                  </Stack>
                </SimpleGrid>

                {isSuperAdmin && (clients.projects || []).length > 0 && (
                  <Stack gap="sm">
                    <Text size="xs" c="dimmed" {...CAPTION}>
                      By project
                    </Text>
                    <Table.ScrollContainer minWidth={720}>
                      <Table verticalSpacing="xs">
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Project</Table.Th>
                            <Table.Th ta="right">Users</Table.Th>
                            <Table.Th ta="right">App</Table.Th>
                            <Table.Th ta="right">Web</Table.Th>
                            <Table.Th ta="right">Both</Table.Th>
                            <Table.Th ta="right">Never seen</Table.Th>
                            <Table.Th ta="right">Active today</Table.Th>
                            <Table.Th ta="right">Old app</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {clients.projects.map((row: any) => (
                            <Table.Tr key={row.project_id}>
                              <Table.Td>
                                <Text size="sm" fw={600}>
                                  {row.project_name || `Project ${row.project_id}`}
                                </Text>
                              </Table.Td>
                              <Table.Td ta="right">{row.employees_total}</Table.Td>
                              <Table.Td ta="right">{row.on_app}</Table.Td>
                              <Table.Td ta="right">{row.on_web}</Table.Td>
                              <Table.Td ta="right">{row.on_both}</Table.Td>
                              <Table.Td ta="right">{row.never_seen}</Table.Td>
                              <Table.Td ta="right">{row.active_today?.any ?? 0}</Table.Td>
                              <Table.Td ta="right">
                                {row.legacy?.app > 0 ? (
                                  <Badge color="orange" variant="light">
                                    {row.legacy.app}
                                  </Badge>
                                ) : (
                                  0
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Stack>
                )}
              </Stack>
            )}
          </Box>
        )}

        {/* What employees recorded, per module */}
        <Stack gap="lg">
          <Group justify="space-between" align="center" gap="md">
            <Group gap="sm" align="baseline">
              <Title order={3} fw={800} lts={-0.3}>
                Activity
              </Title>
              {activity && (
                <Text size="sm" c="dimmed" fw={600}>
                  {activityTotal} record{activityTotal === 1 ? '' : 's'}
                </Text>
              )}
            </Group>
            <SegmentedControl
              radius="xl"
              size="sm"
              value={days}
              onChange={setDays}
              data={PERIODS}
            />
          </Group>

          <SimpleGrid cols={{ base: 2, md: 3, lg: 5 }} spacing={{ base: 'md', sm: 'lg' }}>
            {!activity
              ? Array.from({ length: 10 }, (_, index) => (
                  <Skeleton key={index} h={132} radius={28} />
                ))
              : activity.map((module) => {
                  const target = MODULES[module.key];
                  const Icon = target?.icon || FileText;
                  return (
                    <UnstyledButton
                      key={module.key}
                      onClick={() => target && navigate(target.href)}
                      h="100%"
                    >
                      <Box p="md" h="100%" style={raised(SHADOW_MD, 28)}>
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Center w={42} h={42} c="dimmed" style={raised(SHADOW_SM, 16)}>
                            <Icon size={20} />
                          </Center>
                          <Group gap={4}>
                            {module.queued > 0 && (
                              <Tooltip label="Waiting for Odoo">
                                <Badge color="blue" variant="light" size="sm">
                                  {module.queued}
                                </Badge>
                              </Tooltip>
                            )}
                            {module.failed > 0 && (
                              <Tooltip label="Refused by Odoo">
                                <Badge color="red" variant="light" size="sm">
                                  {module.failed}
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                        </Group>
                        <Text fz={26} fw={800} mt="sm" lh={1.1}>
                          {module.count}
                        </Text>
                        <Text size="xs" c="dimmed" {...CAPTION}>
                          {module.label}
                        </Text>
                      </Box>
                    </UnstyledButton>
                  );
                })}
          </SimpleGrid>
        </Stack>

        {/* Shortcuts */}
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing={{ base: 'md', sm: 'lg' }}>
          {shortcuts.map((shortcut) => (
            <UnstyledButton
              key={shortcut.label}
              onClick={() => navigate(shortcut.href)}
            >
              <Group gap="sm" p="md" wrap="nowrap" style={raised(SHADOW_SM, 22)}>
                <shortcut.icon size={18} />
                <Text size="sm" fw={600} flex={1} truncate>
                  {shortcut.label}
                </Text>
                <ArrowRight size={16} opacity={0.4} />
              </Group>
            </UnstyledButton>
          ))}
        </SimpleGrid>

        {/* Trends */}
        <SimpleGrid cols={{ base: 1, lg: isSuperAdmin ? 2 : 1 }} spacing="xl">
          <SalesGraph />
          {isSuperAdmin && <UsersGraph />}
        </SimpleGrid>
      </Stack>
    </Layout>
  );
};
